/**
 * usePlanRestrictions Hook - Plan-based feature restrictions
 * Provides subscription quota checks and plan-based feature gating
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { billing, auth } from '../services/api/client';
import { useCredits } from './useCredits';

// Plan hierarchy (higher index = more features)
export const PLAN_HIERARCHY = {
  free: 0,
  pro_audit: 1,
  pro: 2,           // Plan Pro standard
  pro_monthly: 2,
  pro_mensuel: 2,
  enterprise: 3,
  entreprise: 3,
  groupe: 4,
  enterprise_plus: 4,
  dedicated: 5,
  superadmin: 999,  // Superadmin has highest level
  sur_mesure: 999   // Sur-Mesure also has full access
};

// Features and their minimum required plan
export const FEATURE_REQUIREMENTS = {
  // Scans
  scan_basic: 'free',
  scan_advanced: 'pro_audit',
  scan_unlimited: 'pro_monthly',
  scan_concurrent: 'enterprise',

  // Workspaces
  workspace_create: 'free',
  workspace_multiple: 'pro_audit',
  workspace_unlimited: 'enterprise',

  // Reports
  report_basic: 'free',
  report_executive: 'pro_audit',
  report_technical: 'pro_audit',
  report_custom: 'enterprise',
  report_white_label: 'dedicated',

  // Team
  team_invite: 'enterprise',
  team_unlimited: 'dedicated',

  // Integrations
  api_access: 'pro_monthly',
  sso_saml: 'enterprise',
  custom_integrations: 'dedicated',

  // Data leak
  data_leak_search: 'pro_audit',

  // Exploitation Sessions (Enterprise+ only)
  exploitation_sessions: 'enterprise',

  // AD Pentest (Enterprise+ only)
  ad_pentest: 'enterprise',

  // Credentials
  credential_validation: 'pro_audit',

  // Advanced tools
  advanced_tools: 'pro_audit',

  // PoC Generation
  poc_generation: 'pro_audit',

  // Export
  export_data: 'pro_audit',

  // Custom reports
  custom_reports: 'pro_audit',

  // Risk Cartography
  risk_cartography: 'pro_audit',

  // Remediation E3R
  remediation_e3r: 'enterprise'
};

// Plan display names
export const PLAN_NAMES = {
  free: 'Plan Gratuit',
  pro_audit: 'Plan Pro Audit',
  pro: 'Plan Pro',
  pro_monthly: 'Plan Pro Mensuel',
  pro_mensuel: 'Plan Pro Mensuel',
  enterprise: 'Plan Entreprise',
  entreprise: 'Plan Entreprise',
  groupe: 'Plan Groupe',
  enterprise_plus: 'Plan Entreprise+',
  dedicated: 'Plan Dédié',
  superadmin: 'Super Admin',
  sur_mesure: 'Sur-Mesure'
};

/**
 * Main plan restrictions hook
 */
export function usePlanRestrictions() {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  const { summary: creditSummary, isExhausted: creditsExhausted, getCreditCost } = useCredits({ autoRefresh: false });

  // Check if user is superadmin or has unlimited plan
  const isSuperAdmin = useMemo(() => {
    return user?.is_superuser === true ||
           user?.role === 'super_admin' ||
           user?.subscription_plan === 'superadmin' ||
           user?.subscription_plan === 'sur_mesure';
  }, [user]);

  // Fetch subscription data
  const fetchSubscription = useCallback(async () => {
    // Skip API calls if not authenticated or token is expired
    if (!auth.isAuthenticated() || !auth.isTokenValidClientSide()) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [subData, usageData, userData] = await Promise.all([
        billing.getSubscription().catch(() => null),
        billing.getUsage().catch(() => null),
        auth.me().catch(() => null)
      ]);
      setSubscription(subData);
      setUsage(usageData);
      setUser(userData);
    } catch (err) {
      // Silently handle 401 errors - user will be redirected to login by API client
      if (err.response?.status !== 401) {
        console.error('Failed to fetch subscription:', err);
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Get current plan - prioritize user's subscription_plan if superadmin
  const currentPlan = useMemo(() => {
    // Superadmin always has full access
    if (isSuperAdmin) {
      return 'superadmin';
    }
    // Check user's subscription_plan from auth/me first
    if (user?.subscription_plan && user.subscription_plan !== 'free') {
      return user.subscription_plan;
    }
    return subscription?.plan || 'free';
  }, [subscription, user, isSuperAdmin]);

  // Check if user has a specific plan or higher
  const hasPlanOrHigher = useCallback((requiredPlan) => {
    // Superadmin always has access
    if (isSuperAdmin) return true;

    const currentLevel = PLAN_HIERARCHY[currentPlan] || 0;
    const requiredLevel = PLAN_HIERARCHY[requiredPlan] || 0;
    return currentLevel >= requiredLevel;
  }, [currentPlan, isSuperAdmin]);

  // Check if a feature is available
  const hasFeature = useCallback((featureName) => {
    // Superadmin always has all features
    if (isSuperAdmin) return true;

    const requiredPlan = FEATURE_REQUIREMENTS[featureName];
    if (!requiredPlan) return true; // Unknown feature = allowed
    return hasPlanOrHigher(requiredPlan);
  }, [hasPlanOrHigher, isSuperAdmin]);

  // Get minimum plan required for a feature
  const getRequiredPlan = useCallback((featureName) => {
    return FEATURE_REQUIREMENTS[featureName] || 'free';
  }, []);

  // Check quota limits - superadmin has unlimited quotas
  const quotas = useMemo(() => {
    if (isSuperAdmin) {
      return {
        max_users: -1,
        max_workspaces: -1,
        max_targets: -1,
        max_scans_per_month: -1,
        max_concurrent_scans: -1,
        max_contracts: -1
      };
    }
    return subscription?.quotas || {
      max_users: 1,
      max_workspaces: 1,
      max_targets: 1,
      max_scans_per_month: 1,
      max_concurrent_scans: 1,
      max_contracts: 1
    };
  }, [subscription, isSuperAdmin]);

  // Check if within quota
  const isWithinQuota = useCallback((quotaType) => {
    // Superadmin has no quota limits
    if (isSuperAdmin) return true;

    if (!usage || !quotas) return true;

    const maxValue = quotas[`max_${quotaType}`];
    if (maxValue === -1) return true; // Unlimited

    const currentValue = usage[quotaType] || usage[`${quotaType}_used`] || 0;
    return currentValue < maxValue;
  }, [usage, quotas, isSuperAdmin]);

  // Get quota status
  const getQuotaStatus = useCallback((quotaType) => {
    if (!usage || !quotas) return { current: 0, max: 1, isUnlimited: false, remaining: 1, percentage: 0 };

    const maxValue = quotas[`max_${quotaType}`];
    const isUnlimited = maxValue === -1;
    const currentValue = usage[quotaType] || usage[`${quotaType}_used`] || 0;

    return {
      current: currentValue,
      max: isUnlimited ? Infinity : maxValue,
      isUnlimited,
      remaining: isUnlimited ? Infinity : Math.max(0, maxValue - currentValue),
      percentage: isUnlimited ? 0 : Math.min(100, (currentValue / maxValue) * 100)
    };
  }, [usage, quotas]);

  // Check if action is restricted (combines plan + quota + credits)
  const checkRestriction = useCallback((action) => {
    const result = {
      isRestricted: false,
      reason: null,
      requiredPlan: null,
      quotaExceeded: null,
      creditsNeeded: null,
      upgradeUrl: '/pricing'
    };

    // Superadmin is never restricted
    if (isSuperAdmin) {
      return result;
    }

    // Check plan requirement
    const featureRequirement = FEATURE_REQUIREMENTS[action];
    if (featureRequirement && !hasPlanOrHigher(featureRequirement)) {
      result.isRestricted = true;
      result.reason = 'plan';
      result.requiredPlan = featureRequirement;
      return result;
    }

    // Check quotas based on action type
    const quotaMapping = {
      scan_basic: 'scans_per_month',
      scan_advanced: 'scans_per_month',
      workspace_create: 'workspaces',
      team_invite: 'users'
    };

    const quotaType = quotaMapping[action];
    if (quotaType && !isWithinQuota(quotaType)) {
      result.isRestricted = true;
      result.reason = 'quota';
      result.quotaExceeded = quotaType;
      return result;
    }

    // Check credits
    const creditCost = getCreditCost(action);
    if (creditCost > 0 && creditSummary) {
      if ((creditSummary.total_available || 0) < creditCost) {
        result.isRestricted = true;
        result.reason = 'credits';
        result.creditsNeeded = creditCost;
        return result;
      }
    }

    return result;
  }, [hasPlanOrHigher, isWithinQuota, getCreditCost, creditSummary, isSuperAdmin]);

  // Get restriction message
  const getRestrictionMessage = useCallback((restriction) => {
    if (!restriction.isRestricted) return null;

    switch (restriction.reason) {
      case 'plan':
        return `Cette fonctionnalite necessite le ${PLAN_NAMES[restriction.requiredPlan] || restriction.requiredPlan}`;
      case 'quota':
        return `Quota atteint pour ${restriction.quotaExceeded.replace(/_/g, ' ')}`;
      case 'credits':
        return `Credits insuffisants (${restriction.creditsNeeded} requis)`;
      default:
        return 'Action non disponible';
    }
  }, []);

  // Check if free plan (simulation mode)
  const isFreePlan = useMemo(() => {
    // Superadmin is never on free plan
    if (isSuperAdmin) return false;
    return currentPlan === 'free';
  }, [currentPlan, isSuperAdmin]);

  // Check if simulation mode
  const isSimulationMode = useMemo(() => {
    // Superadmin never has simulation mode
    if (isSuperAdmin) return false;
    return isFreePlan || subscription?.simulation_only === true;
  }, [isFreePlan, subscription, isSuperAdmin]);

  return {
    // State
    subscription,
    usage,
    loading,
    error,
    user,

    // Plan info
    currentPlan,
    planName: PLAN_NAMES[currentPlan] || currentPlan,
    isFreePlan,
    isSimulationMode,
    isSuperAdmin,

    // Quotas
    quotas,

    // Credit info
    creditSummary,
    creditsExhausted,

    // Checks
    hasPlanOrHigher,
    hasFeature,
    getRequiredPlan,
    isWithinQuota,
    getQuotaStatus,
    checkRestriction,
    getRestrictionMessage,

    // Actions
    refresh: fetchSubscription,

    // Constants
    PLAN_HIERARCHY,
    PLAN_NAMES,
    FEATURE_REQUIREMENTS
  };
}

export default usePlanRestrictions;
